import { forwardRef, InputHTMLAttributes, ReactNode } from 'react'

import { styled } from 'styled-components'

import { InputFormat, InputMask, resolveFormat } from 'core/helpers/masks'
import { Field } from 'core/hooks/useForm'
import useMaskedInput from 'core/hooks/useMaskedInput'

import { common, commonInput, ErrorMessage, Fixture, InputVariant, StyledInput, Wrapper } from './styles'

// Re-exported for existing consumers (DateInput, IdentifyingInformation, ...);
// the styles themselves live in ./styles.ts.
export { common, commonInput, Wrapper }
export type { InputFormat, InputVariant }

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'value' | 'onChange' | 'name'> &
  Field<string> & {
    className?: string
    /** The visual style of the input. */
    variant?: InputVariant
    /** A CSS string defining the width of the input. */
    width?: string
    /** A node to insert before the value text */
    prefix?: ReactNode
    /** A node to insert after the value text */
    postfix?: ReactNode
    /** Controls how the value text is formatted and what characters are allowed.
     * See {@link InputFormat} for the per-value behavior, especially the
     * `digits` vs `integer` distinction. */
    format?: InputFormat
    /**
     * A custom mask (e.g. `cardNumberMask`). The stored value is the RAW
     * value; separators are display-only. Wins over `format` when both are
     * passed.
     */
    mask?: InputMask
    /** Truncates overflowing text with an ellipsis instead of clipping it. */
    truncated?: boolean
  }

/**
 * A text input with several variants and display formats. Note that `format` only
 * controls the display of the text the `value` is always handled as a string.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      prefix,
      postfix,
      variant,
      width,
      disabled,
      className,
      placeholder,
      value,
      onChange,
      onBlur,
      format = 'string',
      mask,
      errorMessage,
      truncated,
      ...props
    },
    ref,
  ) => {
    const resolvedMask = mask ?? resolveFormat(format)
    const { inputProps, prefix: maskPrefix } = useMaskedInput({
      mask: resolvedMask,
      onBlur,
      onChange,
      placeholder,
      ref,
      value,
    })

    const _prefix = prefix ?? maskPrefix

    return (
      <OuterWrapper className={className}>
        {_prefix || postfix ?
          <Wrapper
            $disabled={disabled}
            $errorMessage={!!errorMessage}
            $truncated={truncated}
            $variant={variant}
            $width={width}
          >
            <Fixture>{_prefix}</Fixture>
            <input disabled={disabled} {...props} {...inputProps} />
            <Fixture>{postfix}</Fixture>
          </Wrapper>
        : <StyledInput
            $errorMessage={!!errorMessage}
            $truncated={truncated}
            $variant={variant}
            $width={width}
            disabled={disabled}
            {...props}
            {...inputProps}
          />
        }
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </OuterWrapper>
    )
  },
)

TextInput.displayName = 'TextInput'

export default TextInput

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
